import React, { useState } from "react";
import { DataGridPro, GridColDef, GridRenderCellParams, GridRowParams, GridRowId } from "@mui/x-data-grid-pro";
import { Box, Slide, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Log } from "../../../../types/LogTypes";
import { useLogger } from "../../../../contexts/LoggerContext";
import { theme } from "../../../../styles/Theme";
import { LogDetails } from "../LogDetails/LogDetails";
import { sideDisplayName } from "../../../../helpers/helpers";

interface LogTableProps {
  open: boolean;
}

export const LogTable: React.FC<LogTableProps> = ({ open }) => {
  const { gameLog, deleteLog } = useLogger();
  const [expandedRows, setExpandedRows] = useState<Set<GridRowId>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const handleDelete = async () => {
    console.log("Deleting log:", logToDelete);
    if (logToDelete) {
      setIsDeleting(true);
      try {
        await deleteLog(logToDelete);
      } catch (error) {
        console.error("Error deleting log:", error);
        // You could add a snackbar/toast here to show error message
      } finally {
        setIsDeleting(false);
        setDeleteDialogOpen(false);
        setLogToDelete(null);
      }
    }
  };

  const columns: GridColDef[] = [
    { field: "index", headerName: "#", width: 70 },
    {
      field: "play_type",
      headerName: "Side",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
          `${sideDisplayName(params.row.play_type)}`,
    },
    {
      field: "clubScore",
      headerName: "Club Score",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.game_situation.club_score}`,
    },
    {
      field: "oppScore",
      headerName: "Opponent Score",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.game_situation.opp_score}`,
    },
    {
      field: "qtr",
      headerName: "Qtr",
      width: 100,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.game_situation.quarter}`,
    },
    {
      field: "time",
      headerName: "Time",
      width: 100,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.game_situation.time_remaining}`,
    },
    {
      field: "down",
      headerName: "Down",
      width: 100,
      renderCell: (params: GridRenderCellParams<Log>) =>
        params.row.game_situation.down === -1 ? "-" : `${params.row.game_situation.down}`
    },
    {
      field: "distance",
      headerName: "Distance",
      width: 100,
      renderCell: (params: GridRenderCellParams<Log>) =>
        params.row.game_situation.distance === -1 ? "-" : `${params.row.game_situation.distance}`,
    },
    {
      field: "touchdown",
      headerName: "Touchdown",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.touchdown ? "Yes" : ""}`,
    },
    {
      field: "safety",
      headerName: "Safety",
      width: 120,
      renderCell: (params: GridRenderCellParams<Log>) =>
        `${params.row.safety ? "Yes" : ""}`,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Log>) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation(); // Prevent row expansion
            setLogToDelete(params.row.id?.toString() || null);
            setDeleteDialogOpen(true);
          }}
          size="small"
          color="error"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const rows = gameLog?.logs ? [...gameLog.logs].sort((a, b) => b.index - a.index) : [];

  const handleDetailPanelExpandedRowIdsChange = (newExpandedRows: Set<GridRowId>) => {
    setExpandedRows(newExpandedRows);
  };

  return (
    <>
      <Slide
      direction="up"
      in={open}
      mountOnEnter
      unmountOnExit
      style={{
        maxWidth: 1150,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          height: "100%",
          bgcolor: theme.palette.background.paper,
          boxShadow: 3,
          zIndex: 1000,
          borderTop: 1,
          borderColor: "divider",
          top: 145,
          width: "100%",
        }}
      >
        <DataGridPro
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id || row.index}
          disableRowSelectionOnClick
          getDetailPanelContent={({ row }: GridRowParams<Log>) => (
            <LogDetails log={row} />
          )}
          getDetailPanelHeight={() => 'auto'}
          detailPanelExpandedRowIds={expandedRows}
          onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
          sx={{
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "rgba(123, 45, 58, 0.9)",
              color: "white",
            },
            "& .MuiDataGrid-detailPanel": {
              backgroundColor: "rgba(244, 236, 236, 0.02)",
              borderTop: "1px solid rgba(123, 45, 58, 0.2)",
            },
            "& .MuiDataGrid-detailPanelToggleCell": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-detailPanelToggleCell:hover": {
              backgroundColor: "rgba(123, 45, 58, 0.1)",
            },
          }}
        />
      </Box>
    </Slide>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Log Entry</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this log entry? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
